import { useCallback } from 'react';
import type { QueryMeta } from '../../utils/fetch/types';
import { adaptFileUrlWithoutDomain } from '../../utils/server/adaptFileUrlWithoutDomain';
import getDomain from '../../utils/url/getDomain';
import { isPrivateURL } from '../../utils/url/isPrivateURL';
import { useGetQueryMeta } from '../useGetQueryMeta';
import { adaptFileUrlWithDomain } from './adaptFileUrlWithDomain';

// function for extracting only the path and search query from the URL
const adaptPrivateUrl = (url: string) => {
  // if we have a relative path, we can simply return the path.
  // because we are extracting the path and search query from the URL in this function
  if (!url.startsWith('http') || !isPrivateURL(url)) return url;

  // checking for 3000, 3001, 3002...
  if (process.env.NODE_ENV !== 'development' && !process.env.VERCEL) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    return `${urlObj.pathname}?${urlObj.searchParams.toString()}`;
  } catch (e) {
    console.error('Error parsing URL', e);
    return url;
  }
};

export const adaptFileUrl = (
  _url: string,
  queryMeta: QueryMeta | undefined,
  options: { preview?: boolean } = { preview: true },
) => {
  const domain = getDomain(queryMeta);
  // get adaptedPrivateUrl in case for local and vercel envs.
  const url = adaptPrivateUrl(_url);

  if (!isPrivateURL(url)) {
    // if the url is not private, we can simply return the url
    return url;
  }

  // when domain is not present (e.g. server-side where window is unavailable),
  // do string-based transforms instead of constructing a URL object
  if (!domain) {
    return adaptFileUrlWithoutDomain(url, queryMeta, options);
  }

  return adaptFileUrlWithDomain(url, _url, domain, queryMeta, options);
};

/**
 * This hook is used to adapt the file url.
 * Adds the correct domain(when used in platform or delta-matrix) and auth token to the url.
 * If the interface is public, it replaces the internal api path with the external api path.
 * Applies basePath prefix if provided in queryMeta.
 * @returns A function that adapts the file url.
 */
export const useAdaptFileUrl = () => {
  const queryMeta = useGetQueryMeta();

  return useCallback(
    (url: string, options?: { preview?: boolean }) => {
      return adaptFileUrl(url, queryMeta, options);
    },
    [queryMeta],
  );
};
